import { getUserProfile } from "../api/user.api";
import api from "../api/authApi";
import { pickProfileImageUrl } from "../utils/profileImage";

export async function fetchUserProfile() {
	const raw = await getUserProfile();
	// Try common shapes
	const user = raw?.user || raw || {};
	const name = user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
	const email = user.email || user.mail || "";
	let image = pickProfileImageUrl(user);
	// Fallback: if profile endpoint doesn't include avatar, use last-auth'd user from storage
	if (!image) {
		try {
			const stored = localStorage.getItem("currentUser");
			if (stored) {
				const cu = JSON.parse(stored);
				image = pickProfileImageUrl(cu);
			}
		} catch {}
	}
	const phone = user.phone || user.phone_number || "None";
	const jobTitle = user.job_title || user.title || "";

	// Build data model for AuthorizedInfo component
	const authorizedInfo = {
		description:
			"You can view and update your company’s official identity information here. This information will be used by the system in ordering, invoicing and legal processes.",
		profile: {
			name,
			email,
			image
		},
		fields: [
			{ label: "Job Title", value: jobTitle || "None", action: jobTitle ? "Update" : "Add" },
			{ label: "Name Surname", value: name, action: "Update" },
			{ label: "E-Mail Address", value: email, action: "Update" },
			{ label: "Password", value: "********", action: "Change" },
			{ label: "Phone Number", value: phone || "None", action: phone ? "Update" : "Add" }
		]
	};

	// Address info (derive from profile payload if available; then try explicit address endpoints)
	let addressInfo = user.addressInfo || null;
	const addrSrc =
		user?.address ||
		user?.contact_address ||
		user ||
		{};
	const address_line1 = addrSrc?.address_line1 || addrSrc?.addressLine1 || addrSrc?.address || addrSrc?.street || "";
	const address_line2 = addrSrc?.address_line2 || addrSrc?.addressLine2 || "";
	const city = addrSrc?.city || addrSrc?.district || "";
	const country = addrSrc?.country || "";
	const postal_code = addrSrc?.postal_code || addrSrc?.postalCode || "";
	const state = addrSrc?.state || addrSrc?.province || "";
	const companyEmail = addrSrc?.company_email || addrSrc?.companyEmail || email || "";
	if (address_line1 || city || state || country || postal_code) {
		addressInfo = {
			addressCard: {
				title: "Address",
				address_line1,
				address_line2,
				city,
				state,
				country,
				postal_code,
				companyEmail
			}
		};
	}

	// Do not attempt to GET address from endpoints; backend does not support GET for /user/user/address.

	const locationOptions = { provinces: [], districts: [] };

	return { authorizedInfo, addressInfo, locationOptions, raw };
}

export async function updateUserPhone(phone) {
	const attempts = [
		{ method: "put", body: { phone } },
		{ method: "put", body: { phone_number: phone } }
	];
	let lastErr;
	for (const a of attempts) {
		try {
			const { data } = await api.request({
				url: "/user/user/profile",
				method: a.method,
				data: a.body
			});
			return data;
		} catch (e) {
			lastErr = e;
			continue;
		}
	}
	throw lastErr;
}

export async function updateUserProfile(partial) {
	const { data } = await api.put("/user/user/profile", partial);
	return data;
}

export async function updateUserAddress(addressPartial) {
	// Try multiple method/url/payload variants to match different backends
	// Canonical normalized payload (snake_case)
	const snake = {
		address_line1: addressPartial?.address_line1 || addressPartial?.addressLine1 || "",
		address_line2: addressPartial?.address_line2 ?? addressPartial?.addressLine2 ?? "",
		city: addressPartial?.city || "",
		country: addressPartial?.country || "",
		postal_code: addressPartial?.postal_code || addressPartial?.postalCode || "",
		state: addressPartial?.state || addressPartial?.province || ""
	};

	// Alternate camelCase payload
	const camel = {
		addressLine1: snake.address_line1,
		addressLine2: snake.address_line2,
		city: snake.city,
		country: snake.country,
		postalCode: snake.postal_code,
		state: snake.state
	};

	// Some APIs expect nested "address" object
	const nestedSnake = { address: { ...snake } };
	const nestedCamel = { address: { ...camel } };

	const attempts = [
		{ method: "put", url: "/user/user/address", body: snake },
		{ method: "put", url: "/user/user/profile", body: nestedSnake },
		{ method: "put", url: "/user/user/profile", body: nestedCamel }
	];

	let lastErr;
	for (const attempt of attempts) {
		try {
			const { data } = await api.request({
				method: attempt.method,
				url: attempt.url,
				data: attempt.body,
				params: { check_mfa: true }
			});
			return data;
		} catch (e) {
			lastErr = e;
			const status = e?.response?.status;
			// try next variant on common contract mismatches
			if (status && ![400, 404, 405, 409, 415, 422, 500].includes(status)) break;
			continue;
		}
	}
	throw lastErr;
}

export async function updateUserPassword(current_password, new_password) {
	const payload = { current_password, new_password };
	const { data } = await api.put("/user/user/password", payload);
	return data;
}


